<template>
  <div class="flex flex-col gap-6">
    <!-- Back Button -->
    <LeaderboardEditItem
      :date="date"
      :type="prize.type"
      :enabled="prize.enabled"
      previewing
      @click="emit('reset')"
    />

    <TabsRoot v-model="activeTab" class="flex flex-col gap-6 w-full">
      <!-- Switch -->
      <div class="flex justify-center relative">
        <TabsList
          aria-label="prize-tabs"
          class="relative px-1 flex bg-brand-bgdivider-20 rounded-3xl text-base text-basics-primary font-serif w-full"
        >
          <TabsIndicator
            class="absolute py-1 left-0 h-full bottom-0 top-0 w-[--reka-tabs-indicator-size] translate-x-[--reka-tabs-indicator-position] transition-[width,transform] duration-300"
          >
            <div class="bg-brand-primary h-full rounded-2xl" />
          </TabsIndicator>

          <TabsTrigger
            class="uppercase flex-1 text-sm h-full relative z-10 py-3 line-clamp-1 leading-none select-none outline-none cursor-pointer text-center"
            :value="'prize'"
          >
            {{ $t("adminLeaderboard.prize") }}
          </TabsTrigger>
          <TabsTrigger
            class="uppercase flex-1 text-sm h-full relative z-10 py-3 line-clamp-1 leading-none select-none outline-none cursor-pointer text-center"
            :value="'winners'"
          >
            {{ $t("adminLeaderboard.winners") }}
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="prize" class="flex flex-col gap-6">
        <PrizeFormTab :prize="prizeInput" @save="onPrizeFormSave" />
      </TabsContent>

      <TabsContent value="winners">
        <WinnersTab
          :leaderboard-length="leaderboardLength"
          :start-date="date"
          :end-date="prizeEndDate"
        />
      </TabsContent>
    </TabsRoot>
  </div>
</template>

<script lang="ts">
import { graphql as gql } from "@/generated";

export const LeaderboardPrizeDetailsFragment = gql(/* GraphQL */ `
  fragment LeaderboardPrizeDetailsData on LeaderboardPrize {
    id
    title
    start_date
    type
    enabled
    leaderboard_prize_points {
      id
      points
      from_position
      to_position
    }
  }
`);
</script>

<script setup lang="ts">
import { computed, ref } from "vue";
import {
  TabsContent,
  TabsIndicator,
  TabsList,
  TabsRoot,
  TabsTrigger,
} from "reka-ui";
import LeaderboardEditItem from "./leaderboard-edit-item.vue";
import PrizeFormTab from "./prize-form-tab.vue";
import WinnersTab from "./winners-tab.vue";
import { LeaderboardLength } from "@f3/shared/leaderboard-types";
import {
  LeaderboardPrizeType,
  type UpdateLeaderboardPrizeInput,
} from "@/generated/graphql";
import type { ResultOf } from "@graphql-typed-document-node/core";
import { endOfMonth, endOfWeek } from "@/utils/date";

const emit = defineEmits<{
  save: [UpdateLeaderboardPrizeInput];
  reset: [];
}>();

const props = defineProps<{
  date: Date;
  type: LeaderboardPrizeType;
  leaderboardLength: LeaderboardLength;
  prize: ResultOf<typeof LeaderboardPrizeDetailsFragment>;
}>();

const activeTab = ref<"prize" | "winners">("prize");

const onPrizeFormSave = (data: UpdateLeaderboardPrizeInput) => {
  emit("save", data);
};

const prizeEndDate = computed(() => {
  const endDate =
    props.prize.type === LeaderboardPrizeType.Weekly
      ? endOfWeek(props.date, 1)
      : endOfMonth(props.date);
  return endDate;
});

const prizeInput = computed(() => {
  return {
    id: props.prize.id,
    type: props.prize.type,
    title: props.prize.title,
    leaderboard_prize_points: props.prize.leaderboard_prize_points,
  } as UpdateLeaderboardPrizeInput;
});
</script>
